class CollectionSliderFilter extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[data-csf-panel]'));
    if (this.panels.length === 0) return;

    this.toggle = this.querySelector('[data-csf-toggle]');
    this.icon = this.querySelector('[data-csf-icon]');
    this.menu = this.querySelector('[data-csf-menu]');
    this.label = this.querySelector('[data-csf-label]');
    this.status = this.querySelector('[data-csf-status]');
    this.options = Array.from(this.querySelectorAll('[data-csf-option]'));

    this.allLabel = this.dataset.allLabel || 'All collections';
    this.openLabel = this.dataset.openLabel || 'Choose a collection';
    this.clearLabel = this.dataset.clearLabel || 'Clear';
    this.scrollOnChange = this.dataset.scrollOnChange === 'true';
    this.gridSection = this.dataset.gridSection || 'product-grid';
    this.selected = '';

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeyup = this.onKeyup.bind(this);

    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.setMenu(!this.isOpen()));
    }

    // One button, two jobs: it opens the list while nothing is chosen, and
    // becomes the clear control once a collection is selected.
    if (this.icon) {
      this.icon.addEventListener('click', () => {
        if (this.selected) this.apply('', true);
        else this.setMenu(!this.isOpen());
      });
    }

    this.options.forEach((option) => {
      option.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        this.apply(option.dataset.csfOption, true);
      });
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keyup', this.onKeyup);

    this.apply('', false);
    this.observeLazyPanels();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onKeyup);
    if (this.lazyObserver) this.lazyObserver.disconnect();
  }

  /* ------------------------------------------------- lazy slider contents */

  observeLazyPanels() {
    const lazy = this.querySelectorAll('[data-csf-lazy]');
    if (lazy.length === 0) return;

    this.lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.lazyObserver.unobserve(entry.target);
          this.fill(entry.target);
        });
      },
      { rootMargin: '800px 0px' }
    );

    lazy.forEach((panel) => this.lazyObserver.observe(panel));
  }

  async fetchItems(url) {
    // Preferred: the Section Rendering API returns just the grid markup.
    // Fallback: the collection page itself, for templates whose grid section
    // carries a different ID.
    const attempts = [`${url}?section_id=${this.gridSection}`, url];

    for (const attempt of attempts) {
      try {
        const response = await fetch(attempt);
        if (!response.ok) continue;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = Array.from(doc.querySelectorAll('#product-grid .grid__item'));
        if (items.length > 0) return items;

        console.warn(`collection-slider-filter: no products found at ${attempt}`);
      } catch (error) {
        console.warn(`collection-slider-filter: request failed for ${attempt}`, error);
      }
    }

    return [];
  }

  async fill(panel) {
    const url = panel.dataset.csfLazy;
    const slots = Array.from(panel.querySelectorAll('[data-csf-slot]'));
    if (!url || slots.length === 0) return;

    const items = await this.fetchItems(url);

    if (items.length === 0) {
      this.drop(panel);
      return;
    }

    slots.forEach((slot, index) => {
      const item = items[index];
      // Fewer products than slots: drop the leftovers so the slider does not
      // page through empty cells.
      if (!item) {
        slot.remove();
        return;
      }
      slot.innerHTML = item.innerHTML;
      slot.removeAttribute('data-csf-slot');
    });

    panel.removeAttribute('data-csf-lazy');
  }

  // A row we cannot fill would sit there as empty grey boxes, and its dropdown
  // entry would select nothing. Remove both.
  drop(panel) {
    const handle = panel.dataset.csfPanel;
    this.panels = this.panels.filter((candidate) => candidate !== panel);
    this.options = this.options.filter((option) => {
      if (option.dataset.csfOption !== handle) return true;
      option.remove();
      return false;
    });
    panel.remove();
  }

  isOpen() {
    return this.menu ? !this.menu.hidden : false;
  }

  setMenu(open) {
    if (!this.menu) return;
    this.menu.hidden = !open;
    if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
  }

  onDocumentClick(event) {
    if (!this.isOpen() || this.contains(event.target)) return;
    this.setMenu(false);
  }

  onKeyup(event) {
    if (event.key !== 'Escape' || !this.isOpen()) return;
    this.setMenu(false);
    if (this.toggle) this.toggle.focus();
  }

  apply(handle, userInitiated) {
    const match = handle ? this.panels.find((panel) => panel.dataset.csfPanel === handle) : null;
    if (handle && !match) return;

    this.selected = match ? handle : '';

    this.panels.forEach((panel) => {
      panel.hidden = this.selected ? panel.dataset.csfPanel !== this.selected : false;
    });

    const title = match ? match.dataset.csfTitle || handle : this.allLabel;
    if (this.label) this.label.textContent = title;

    this.classList.toggle('csf--selected', Boolean(this.selected));

    if (this.icon) {
      this.icon.setAttribute('aria-label', this.selected ? this.clearLabel : this.openLabel);
    }

    this.options.forEach((option) => {
      if (this.selected && option.dataset.csfOption === this.selected) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    });

    if (this.status) {
      this.status.textContent = this.selected
        ? title
        : `${this.allLabel}, ${this.panels.length} collections`;
    }

    if (!userInitiated) return;

    this.setMenu(false);

    if (this.scrollOnChange) {
      const offset = parseInt(this.dataset.stickyOffset || '0', 10);
      const top = window.scrollY + this.getBoundingClientRect().top - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
  }
}

if (!customElements.get('collection-slider-filter')) {
  customElements.define('collection-slider-filter', CollectionSliderFilter);
}
